import React, { useEffect } from "react";
import { navigate, RouteComponentProps } from "@reach/router";
import { useMatchParams } from "kanopi-components/hooks/useMatchParams";
import { $ } from "kw/api";
import { quoteFlowFields } from "kw/api/fragments";
import {
  QuestionFlowComponent,
  QuestionFlowTypeEnum,
} from "kw/components/QuestionFlow/QuestionFlowComponent";
import { useAppLayoutContext } from "kw/components/AppLayout/appLayoutContext";
import { usePolicy } from "kw/policies/hooks/usePolicy";
import { Placeholder } from "kanopi-components/Placeholder";
import { useUser } from "kw/auth/useUser";

type TPolicyHolderFlowPage = {
  riskProfileId?: string;
  policyId?: string;
};
export const PolicyHolderFlowPage: React.FC<
  TPolicyHolderFlowPage & RouteComponentProps
> = ({ riskProfileId, policyId, uri: prefix, location }) => {
  const { questionSetKey } = useMatchParams(`${prefix}/:questionSetKey`);
  const { context, setContext } = useAppLayoutContext();
  const isUpdateFlow = policyId ? true : false;
  const [isPreviousFlowEmpty] = React.useState(location?.state?.isPreviousFlowEmpty);
  const [policy, { policyLoading }] = usePolicy(policyId);
  const user = useUser();
  const startPolicyholderQuery = {
    startPolicyholder: [
      {
        input: $("input", "StartPolicyHolderInput"),
      },
      { ...quoteFlowFields },
    ],
  };
  const startPolicyHolderQueryParams = {
    input: {
      riskProfileId: riskProfileId || policy?.riskProfile?.id,
      questionSetKey,
    },
  };
  const [currentMaxSet, setCurrentMaxSet] = React.useState({
    currentSet: 0,
    maxSets: 1,
  });
  const onCurrentMaxSetChange = (currentSet, maxSets) => {
    const _totalSets = { ...currentMaxSet };
    _totalSets.currentSet = !questionSetKey && currentSet === maxSets ? 0 : currentSet;
    _totalSets.maxSets = maxSets;
    setCurrentMaxSet(_totalSets);
  };

  useEffect(() => {
    /* Context may be updated multiple times (in parent components) during single render.
    Wrapping in setTimeout to ensure this update outside of React's state updates batch
    */
    if (isUpdateFlow) {
      setTimeout(() => {
        setContext({
          ...context,
          showHeader: true,
          headerText: "Edit Policy Holder",
          backButtonClick: () => {
            navigate(`/policies/${policyId}/update`);
          },
        });
      });
    }
  }, []);
  return isUpdateFlow ? (
    <Placeholder loading={policyLoading || !policy?.riskProfile?.id}>
      <QuestionFlowComponent
        isFlowLayout={false}
        actionFlowType={QuestionFlowTypeEnum.PolicyHolder}
        prefix={prefix}
        finishedRoute={`/policies/${policyId}/update`}
        nextButtonText={
          currentMaxSet.currentSet > 0 &&
          currentMaxSet.currentSet !== currentMaxSet.maxSets
            ? "Next"
            : "Submit"
        }
        defaultBackLocation={`/policies/${policyId}/update`}
        actionQuery={startPolicyholderQuery}
        actionQueryParams={startPolicyHolderQueryParams}
        questionFlowResponseKey="startPolicyholder"
        questionSetKey={questionSetKey}
        onCurrentAndMaxSetChange={onCurrentMaxSetChange}
      />
    </Placeholder>
  ) : (
    <QuestionFlowComponent
      isFlowLayout={true}
      actionFlowType={QuestionFlowTypeEnum.PolicyHolder}
      prefix={prefix}
      finishedRoute={
        user.isLazy
          ? `/quoting/${riskProfileId}/password-less-register`
          : `/quoting/payment/${riskProfileId}/`
      }
      defaultBackLocation={
        isPreviousFlowEmpty ? "/quote/ready" : `/quoting/knockout/${riskProfileId}`
      }
      actionQuery={startPolicyholderQuery}
      actionQueryParams={startPolicyHolderQueryParams}
      questionFlowResponseKey="startPolicyholder"
      questionSetKey={questionSetKey}
      steps={[
        {
          label: "Your details",
          complete: true,
        },
        { label: "Your quote", complete: true },
        {
          label: "Review and buy",
          active: true,
          current: currentMaxSet.currentSet,
          total: currentMaxSet.maxSets + 2,
        },
      ]}
      onCurrentAndMaxSetChange={onCurrentMaxSetChange}
    />
  );
};
